/**
 * RecordHistoryDialog Component
 * Displays audit history for master data records
 * Shows Created/Modified dates, times, and users
 */

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Clock } from 'lucide-react';

/**
 * Format ISO date string to display format (DD-MMM-YYYY)
 */
const formatDate = (isoString) => {
  if (!isoString) return 'Not Available';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, '-');
  } catch {
    return 'Not Available';
  }
};

/**
 * Format ISO date string to time format (HH:MM AM/PM)
 */
const formatTime = (isoString) => {
  if (!isoString) return 'Not Available';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return 'Not Available';
  }
};

/**
 * Format username for display
 */
const formatUser = (username) => {
  if (!username) return 'Not Available';
  return username;
};

const RecordHistoryDialog = ({
  open,
  onOpenChange,
  record,
  title = 'Record History'
}) => {
  const hasModified = record?.updated_at || record?.updated_by;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[320px] p-0 gap-0 bg-white" data-testid="record-history-dialog">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b bg-maroon-50">
          <DialogTitle className="text-base font-semibold text-maroon-800 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View creation and modification history for this record
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Created Section */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-maroon-600 uppercase tracking-wide">
              Created
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="text-gray-500">Date</div>
              <div className="font-medium text-gray-900" data-testid="created-date">
                {formatDate(record?.created_at)}
              </div>
              <div className="text-gray-500">Time</div>
              <div className="font-medium text-gray-900" data-testid="created-time">
                {formatTime(record?.created_at)}
              </div>
              <div className="text-gray-500">By</div>
              <div className="font-medium text-gray-900" data-testid="created-by">
                {formatUser(record?.created_by)}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Modified Section */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-maroon-600 uppercase tracking-wide">
              Last Modified
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="text-gray-500">Date</div>
              <div className="font-medium text-gray-900" data-testid="modified-date">
                {hasModified ? formatDate(record?.updated_at) : 'Not Modified'}
              </div>
              <div className="text-gray-500">Time</div>
              <div className="font-medium text-gray-900" data-testid="modified-time">
                {hasModified ? formatTime(record?.updated_at) : '—'}
              </div>
              <div className="text-gray-500">By</div>
              <div className="font-medium text-gray-900" data-testid="modified-by">
                {hasModified ? formatUser(record?.updated_by) : '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500 text-center">
          Press Esc or click outside to close
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecordHistoryDialog;
